/**
 * Background music bar for the Move workout player.
 *
 * • Starts ONLY when the user presses play (no autoplay).
 * • Prev / next switch track; on track end → next in loop.
 * • Heart marks the current track as favorite; the star filter limits
 *   the playlist to favorites only.
 * Admin uploads tracks in Admin → Workout Move → Musica.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getMoveMusic, toggleMoveMusicFavorite, reportMoveMusicUsage } from '../lib/api';
import { API_URL } from '../config/api';
import { useTranslation } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Play, Pause, SkipBack, SkipForward, Heart, Star, Music, Lock, Crown } from 'lucide-react';
import { toast } from 'sonner';

const trackUrl = (t) => (t.audio_url?.startsWith('/api') ? `${API_URL}${t.audio_url}` : t.audio_url);

export default function MoveMusicBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [source, setSource] = useState('uploaded');
  const [moods, setMoods] = useState([]);
  const [mood, setMood] = useState('energia');
  const [currentId, setCurrentId] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [favOnly, setFavOnly] = useState(false);
  // Premium gate: free users get N minutes of music per month
  const [premium, setPremium] = useState(true);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const audioRef = useRef(null);
  const playlistRef = useRef([]);
  const playingRef = useRef(false);
  playingRef.current = playing;

  const applyAccess = (a) => {
    if (!a) return;
    setPremium(!!a.premium);
    if (!a.premium) setSecondsLeft(a.free_seconds_left ?? 0);
  };

  const lockNow = useCallback(() => {
    const a = audioRef.current;
    if (a) { a.pause(); a.src = ''; }
    setPlaying(false);
    setSecondsLeft(0);
  }, []);

  // Usage heartbeat: every 15s of actual playback (free users only)
  useEffect(() => {
    if (premium || !playing) return undefined;
    const id = setInterval(async () => {
      if (!playingRef.current) return;
      try {
        const r = await reportMoveMusicUsage(15);
        const d = r.data || {};
        if (!d.premium) {
          setSecondsLeft(d.free_seconds_left ?? 0);
          if (d.locked) lockNow();
        }
      } catch (_) { /* noop */ }
    }, 15000);
    return () => clearInterval(id);
  }, [premium, playing, lockNow]);

  const loadTracks = useCallback((m) => {
    getMoveMusic(m)
      .then((r) => {
        const d = r.data || {};
        setTracks(d.tracks || []);
        setSource(d.source || 'uploaded');
        setMoods(d.moods || []);
        if (d.mood) setMood(d.mood);
        applyAccess(d.music_access);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { loadTracks(); }, [loadTracks]);

  const changeMood = async (m) => {
    if (m === mood) return;
    setMood(m);
    setFavOnly(false);
    try {
      const r = await getMoveMusic(m);
      const d = r.data || {};
      const list = d.tracks || [];
      setTracks(list);
      setSource(d.source || 'uploaded');
      setMoods(d.moods || []);
      setCurrentId(null);
      // If music is playing, switch immediately to the first track of
      // the new playlist so the title always matches the audio.
      if (playing && list.length > 0) {
        startTrack(list[0], true);
      } else {
        // Paused: drop the old src so the next play uses the new list.
        const a = audioRef.current;
        if (a) { a.pause(); a.src = ''; }
        setPlaying(false);
      }
    } catch (_) { /* noop */ }
  };

  const playlist = useMemo(() => {
    const list = favOnly ? tracks.filter((x) => x.is_favorite) : tracks;
    return list.length > 0 ? list : tracks;
  }, [tracks, favOnly]);
  playlistRef.current = playlist;

  const current = playlist.find((x) => x.track_id === currentId) || playlist[0];

  const getAudio = () => {
    if (!audioRef.current) {
      const a = new Audio();
      a.addEventListener('ended', () => skip(1, true));
      audioRef.current = a;
    }
    return audioRef.current;
  };

  const startTrack = useCallback((track, autoplay = true) => {
    if (!track) return;
    const a = getAudio();
    a.src = trackUrl(track);
    setCurrentId(track.track_id);
    if (autoplay) {
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skip = useCallback((dir, keepPlaying = false) => {
    const list = playlistRef.current;
    if (list.length === 0) return;
    setCurrentId((prevId) => {
      const idx = Math.max(0, list.findIndex((x) => x.track_id === prevId));
      const next = list[(idx + dir + list.length) % list.length];
      const a = getAudio();
      a.src = trackUrl(next);
      if (keepPlaying || !a.paused || playing) {
        a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      }
      return next.track_id;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  const togglePlay = () => {
    if (!current) return;
    const a = getAudio();
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      if (!a.src) a.src = trackUrl(current);
      if (!currentId) setCurrentId(current.track_id);
      a.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const toggleFavorite = async () => {
    if (!current) return;
    try {
      const res = await toggleMoveMusicFavorite(current.track_id);
      setTracks((prev) => prev.map((x) => (x.track_id === current.track_id ? { ...x, is_favorite: res.data.is_favorite } : x)));
    } catch (_) { /* noop */ }
  };

  const toggleFavOnly = () => {
    if (!favOnly && !tracks.some((x) => x.is_favorite)) {
      toast(t('move.music.noFavorites', 'Nessuna traccia preferita'));
      return;
    }
    setFavOnly((v) => !v);
  };

  // Stop the music when the player unmounts (exit / workout completed)
  useEffect(() => () => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.src = '';
    }
  }, []);

  if (tracks.length === 0 && source !== 'audius') return null;

  const locked = !premium && secondsLeft !== null && secondsLeft <= 0;
  const minutesLeft = secondsLeft !== null ? Math.ceil(secondsLeft / 60) : null;

  // Locked state — free minutes exhausted → Premium upsell
  if (locked) {
    return (
      <div className="mx-4 mb-2 px-3 py-2.5 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-2.5"
        data-testid="move-music-locked">
        <div className="w-9 h-9 rounded-full flex items-center justify-center bg-white/10 flex-shrink-0">
          <Lock className="w-4 h-4 text-white/70" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white/90 text-xs font-semibold truncate">
            {t('move.music.lockedTitle', 'Minuti gratuiti esauriti')}
          </p>
          <p className="text-white/50 text-[11px] truncate">
            {t('move.music.lockedSubtitle', 'La musica è illimitata con Premium')}
          </p>
        </div>
        <button onClick={() => navigate('/app/premium')}
          className="px-3.5 py-2 rounded-xl text-white text-xs font-bold flex items-center gap-1.5 flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #E8334A 0%, #F59E0B 100%)' }}
          data-testid="move-music-premium-btn">
          <Crown className="w-3.5 h-3.5" />
          {t('move.music.goPremium', 'Passa a Premium')}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-4 mb-2" data-testid="move-music-wrap">
      {/* Mood chips (Audius source only) */}
      {source === 'audius' && moods.length > 0 && (
        <div className="flex gap-1.5 mb-1.5" data-testid="move-music-moods">
          {moods.map((m) => (
            <button key={m} onClick={() => changeMood(m)}
              className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                mood === m ? 'text-white' : 'bg-white/5 text-white/60'
              }`}
              style={mood === m ? { background: 'linear-gradient(135deg, #E8334A 0%, #F59E0B 100%)' } : {}}
              data-testid={`move-music-mood-${m}`}>
              {t(`move.music.moods.${m}`, m)}
            </button>
          ))}
        </div>
      )}
      <div className="px-3 py-2 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-2"
        data-testid="move-music-bar">
      <Music className="w-4 h-4 text-white/50 flex-shrink-0" />
      <div className="flex-1 min-w-0 overflow-hidden" data-testid="move-music-title">
        {current && (
          <div className="move-marquee text-white/80 text-xs font-medium whitespace-nowrap" key={current.track_id}>
            <span className="pr-8">{current.title}</span>
            <span className="pr-8" aria-hidden="true">{current.title}</span>
          </div>
        )}
      </div>
      <button onClick={toggleFavorite} className="p-1.5" data-testid="move-music-fav-btn"
        title={t('move.music.favorite', 'Preferito')}>
        <Heart className={`w-4 h-4 ${current?.is_favorite ? 'text-[#E8334A] fill-[#E8334A]' : 'text-white/50'}`} />
      </button>
      <button onClick={toggleFavOnly} className="p-1.5" data-testid="move-music-favonly-btn"
        title={t('move.music.onlyFavorites', 'Solo preferiti')}>
        <Star className={`w-4 h-4 ${favOnly ? 'text-[#F59E0B] fill-[#F59E0B]' : 'text-white/50'}`} />
      </button>
      <button onClick={() => skip(-1)} className="p-1.5 text-white/70" data-testid="move-music-prev-btn">
        <SkipBack className="w-4 h-4" />
      </button>
      <button onClick={togglePlay}
        className="w-9 h-9 rounded-full flex items-center justify-center text-white flex-shrink-0"
        style={{ background: 'linear-gradient(135deg, #E8334A 0%, #F59E0B 100%)' }}
        data-testid="move-music-play-btn">
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
      <button onClick={() => skip(1)} className="p-1.5 text-white/70" data-testid="move-music-next-btn">
        <SkipForward className="w-4 h-4" />
      </button>
      </div>
      {/* Free minutes counter (non-premium) */}
      {!premium && minutesLeft !== null && (
        <p className="text-white/40 text-[10px] mt-1 px-1" data-testid="move-music-free-left">
          {t('move.music.freeLeft', '{min} min di musica gratis rimasti questo mese').replace('{min}', String(minutesLeft))}
        </p>
      )}
    </div>
  );
}
